import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCountdown } from '../../hooks/useCountdown';
import { usePresaleStore } from '../../store/presaleSlice';
import { useWalletStore } from '../../store/walletSlice';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { WalletModal } from '../web3/WalletModal';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';

export const PresalePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { days, hours, minutes, seconds } = useCountdown('2025-10-31T23:59:59Z');
  const { phases, currentPhase, stats } = usePresaleStore();
  const { wallet, account, balance, connectWallet, disconnectWallet } = useWalletStore();
  const [isWalletModalOpen, setIsWalletModalOpen] = React.useState(false);
  const [idrAmount, setIdrAmount] = React.useState<number>(100000);
  const [tokenAmount, setTokenAmount] = React.useState<number>(0);
  const [suiAddress, setSuiAddress] = React.useState<string>('');
  const trialItems = t('presale.disclaimer.items', { returnObjects: true }) as string[];

  const openWalletModal = () => setIsWalletModalOpen(true);
  const closeWalletModal = () => setIsWalletModalOpen(false);

  const handleConnectWallet = (walletName: string) => {
    connectWallet(walletName);
    closeWalletModal();
  };

  const handleDisconnectWallet = () => {
    disconnectWallet();
  };

  // Calculate token amount from IDR
  React.useEffect(() => {
    const tokenPrice = 26000; // 1 IDNGOLD = Rp 26,000
    const calculatedTokens = idrAmount / tokenPrice;
    setTokenAmount(calculatedTokens);
  }, [idrAmount]);

  // Handle Purchase with IDR
  const handlePurchaseWithIDR = () => {
    if (idrAmount < 100000) {
      alert(t('presale.alerts.minPurchaseIdr'));
      return;
    }

    if (!suiAddress || suiAddress.length !== 66 || !suiAddress.startsWith('0x')) {
      alert(t('presale.alerts.invalidSuiAddress'));
      return;
    }

    // Navigate to payment page with payment data
    navigate('/payment', {
      state: {
        amount: idrAmount / 15800, // Convert IDR to USD for display
        tokenAmount: tokenAmount,
        phase: currentPhase + 1,
        pricePerToken: stats.currentPrice,
        suiAddress: suiAddress,
      }
    });
  };

  // Simulate dynamic updates
  React.useEffect(() => {
    const interval = setInterval(() => {
      usePresaleStore.getState().updateStats();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          {/* Trial Phase Banner */}
          <div className="mb-8">
            <div className="hero-badge inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold/20 to-indonesia-red/20 border border-gold/30 rounded-full text-sm font-bold text-gold mb-4 group">
              <i className="fas fa-flask group-hover:rotate-12 transition-transform"></i>
              <span>{t('presale.hero.trialBadge')}</span>
            </div>
            <p className="text-gray-400 text-lg">{t('presale.hero.trialMessage')}</p>
          </div>

          <div className="hero-badge inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold/20 to-indonesia-red/20 border border-gold/30 rounded-full text-sm font-bold text-gold mb-8 group">
            <i className="fas fa-fire group-hover:scale-110 transition-transform"></i>
            <span>{t('presale.hero.limitedBadge')}</span>
          </div>

          <h1 className="hero-title text-4xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('presale.hero.title')}
          </h1>

          <p className="hero-subtitle text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            {t('presale.hero.subtitle')}
          </p>

          {/* Live Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
            <div className="metric-counter group">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2 tabular-nums group-hover:scale-110 transition-transform">
                {stats.tokensSold.toLocaleString()}+
              </div>
              <div className="text-gray-400 font-medium">{t('presale.hero.stats.tokensSold')}</div>
            </div>

            <div className="metric-counter group">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2 tabular-nums group-hover:scale-110 transition-transform">
                {stats.tokensRemaining.toLocaleString()}+
              </div>
              <div className="text-gray-400 font-medium">{t('presale.hero.stats.tokensRemaining')}</div>
            </div>

            <div className="metric-counter group">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2 group-hover:scale-110 transition-transform">
                {stats.currentPrice}
              </div>
              <div className="text-gray-400 font-medium">{t('presale.hero.stats.currentPrice')}</div>
            </div>
          </div>

          {/* Countdown Timer */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">
                {t('presale.hero.countdown.title', { phase: currentPhase + 1 })} Ends In
              </span>
            </div>
            <div className="countdown-timer grid grid-cols-4 gap-4">
              <div className="countdown-item glass-card text-center group">
                <div className="countdown-value text-4xl md:text-5xl font-black text-gold mb-2 group-hover:scale-110 transition-transform">
                  {days.toString().padStart(2, '0')}
                </div>
                <div className="countdown-label text-sm text-gray-400">{t('presale.hero.countdown.labels.days')}</div>
              </div>
              <div className="countdown-item glass-card text-center group">
                <div className="countdown-value text-4xl md:text-5xl font-black text-gold mb-2 group-hover:scale-110 transition-transform">
                  {hours.toString().padStart(2, '0')}
                </div>
                <div className="countdown-label text-sm text-gray-400">{t('presale.hero.countdown.labels.hours')}</div>
              </div>
              <div className="countdown-item glass-card text-center group">
                <div className="countdown-value text-4xl md:text-5xl font-black text-gold mb-2 group-hover:scale-110 transition-transform">
                  {minutes.toString().padStart(2, '0')}
                </div>
                <div className="countdown-label text-sm text-gray-400">{t('presale.hero.countdown.labels.minutes')}</div>
              </div>
              <div className="countdown-item glass-card text-center group">
                <div className="countdown-value text-4xl md:text-5xl font-black text-gold mb-2 group-hover:scale-110 transition-transform">
                  {seconds.toString().padStart(2, '0')}
                </div>
                <div className="countdown-label text-sm text-gray-400">{t('presale.hero.countdown.labels.seconds')}</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="flex justify-between items-center mb-3 text-sm">
              <span className="text-gray-400">{t('presale.hero.progress.title', { phase: currentPhase + 1 })}</span>
              <span className="text-gold font-bold">{t('presale.hero.progress.sold', { percentage: stats.percentageSold.toFixed(1) })}</span>
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-gold to-gold-light transition-all duration-500 rounded-full relative overflow-hidden"
                style={{ width: `${stats.percentageSold}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20 animate-shimmer"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Presale Phases */}
      <section className="section relative bg-bg-secondary/50">
        <div className="container">
          <div className="text-center mb-20">
            <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('presale.phases.title')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('presale.phases.title')}</h2>
          </div>

          <div className="space-y-6 max-w-5xl mx-auto">
            {phases.map((phase, index) => {
              const phaseKey = `presale.phases.items.${phase.id}`;
              return (
                <div
                  key={index}
                  className={clsx(
                    "modern-card group",
                    phase.isActive ? "ring-2 ring-gold/50" : ""
                  )}
                >
                  <div className="flex items-center gap-6">
                    <div className={`step-number ${phase.isActive ? 'bg-gradient-to-r from-gold to-gold-light' : 'bg-gray-600'} group-hover:scale-110 transition-transform`}>
                      {index + 1}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-2xl font-bold text-white group-hover:text-gold transition-colors">
                          {t(`${phaseKey}.title`)}
                        </h3>
                        <div className={clsx(
                          "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider",
                          phase.status === 'active'
                            ? "bg-green-500/20 text-green-400 border border-green-500/30"
                            : "bg-gray-600/20 text-gray-400 border border-gray-600/30"
                        )}>
                          {t(`presale.phases.status.${phase.status}`)}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-3 bg-gold/10 rounded-lg">
                          <div className="text-sm text-gray-400 mb-1">Allocation</div>
                          <div className="text-lg font-bold text-gold">
                            {t('presale.phases.allocation', { amount: phase.allocation })}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-gold/10 rounded-lg">
                          <div className="text-sm text-gray-400 mb-1">Price</div>
                          <div className="text-lg font-bold text-gold">
                            {t('presale.phases.price', { price: phase.price })}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-gold/10 rounded-lg">
                          <div className="text-sm text-gray-400 mb-1">Status</div>
                          <div className="text-lg font-bold text-gold">
                            {phase.status === 'active' ? 'Open' : 'Closed'}
                          </div>
                        </div>
                      </div>

                      <p className="text-gray-400 leading-relaxed">
                        {t(`${phaseKey}.description`)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('presale.purchase.title')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('presale.purchase.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Choose your preferred payment method to participate in the presale
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Web3 Purchase */}
            <div className="partner-card group">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="fas fa-wallet text-4xl text-gold"></i>
                </div>
                <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                  <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('presale.purchase.web3.badge')}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {t('presale.purchase.web3.title')}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {t('presale.purchase.web3.description')}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center p-3 bg-gold/5 rounded-lg">
                  <span className="text-gray-400">{t('presale.purchase.web3.rate.exchangeLabel')}</span>
                  <span className="text-gold font-bold">{t('presale.purchase.web3.rate.exchangeValue')}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gold/5 rounded-lg">
                  <span className="text-gray-400">{t('presale.purchase.web3.rate.paymentLabel')}</span>
                  <span className="text-gold font-bold">{t('presale.purchase.web3.rate.paymentValue')}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gold/5 rounded-lg">
                  <span className="text-gray-400">{t('presale.purchase.web3.rate.priceLabel')}</span>
                  <span className="text-gold font-bold">{t('presale.purchase.web3.rate.priceValue')}</span>
                </div>
              </div>

              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-6">
                <p className="text-blue-400 text-sm">
                  <i className="fas fa-info-circle mr-2"></i>
                  {t('presale.purchase.web3.notice')}
                </p>
              </div>

              {wallet ? (
                <div className="space-y-4">
                  <div className="text-left p-4 bg-gold/10 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">{t('presale.purchase.web3.connected.address')}</div>
                    <div className="font-mono text-gold text-sm">{account?.address}</div>
                    <div className="text-sm text-gray-400 mt-2">{t('presale.purchase.web3.connected.balance')}</div>
                    <div className="text-gold font-bold">{balance} USDC</div>
                  </div>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={handleDisconnectWallet}
                  >
                    {t('presale.purchase.web3.disconnect')}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="primary"
                  className="w-full group"
                  onClick={openWalletModal}
                >
                  <span className="flex items-center justify-center">
                    <i className="fas fa-wallet mr-3 group-hover:rotate-12 transition-transform"></i>
                    {t('presale.modal.title')}
                  </span>
                </Button>
              )}
            </div>

            {/* Fiat Purchase */}
            <div className="partner-card group">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="fas fa-credit-card text-4xl text-gold"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {t('presale.purchase.fiat.title')}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {t('presale.purchase.fiat.description')}
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-gray-400 mb-2">{t('presale.purchase.fiat.calculator.idrLabel')}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={idrAmount}
                      onChange={(e) => setIdrAmount(Number(e.target.value))}
                      placeholder={t('presale.purchase.fiat.calculator.idrPlaceholder')}
                      className="w-full px-4 py-3 bg-gold/10 border border-gold/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">IDR</span>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 mb-2">{t('presale.purchase.fiat.calculator.tokenLabel')}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={tokenAmount}
                      readOnly
                      placeholder={t('presale.purchase.fiat.calculator.tokenPlaceholder')}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">IDNG</span>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 mb-2">{t('presale.purchase.fiat.calculator.walletLabel')}</label>
                  <input
                    type="text"
                    value={suiAddress}
                    onChange={(e) => setSuiAddress(e.target.value)}
                    placeholder={t('presale.purchase.fiat.calculator.walletPlaceholder')}
                    className="w-full px-4 py-3 bg-gold/10 border border-gold/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all"
                  />
                  <p className="text-xs text-gray-400 mt-2">{t('presale.purchase.fiat.calculator.walletHelp')}</p>
                </div>

                <div className="p-4 bg-gold/10 border border-gold/20 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">{t('presale.purchase.fiat.calculator.rateNote')}</span>
                    <span className="text-gold font-bold">1 IDNGOLD = Rp 26,000</span>
                  </div>
                  <div className="text-xs text-gray-400">{t('presale.purchase.fiat.calculator.minimumNote')}</div>
                </div>

                <Button
                  variant="primary"
                  className="w-full group"
                  onClick={handlePurchaseWithIDR}
                >
                  <span className="flex items-center justify-center">
                    <i className="fas fa-shopping-cart mr-3 group-hover:rotate-12 transition-transform"></i>
                    {t('presale.purchase.fiat.button')}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="section relative bg-bg-secondary/50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="cta-section relative border-2 border-yellow-500/20">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-exclamation-triangle text-4xl text-yellow-400"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
                  {t('presale.disclaimer.title')}
                </h2>
                <p className="text-yellow-200 mb-8">
                  {t('presale.disclaimer.intro')}
                </p>

                <div className="glass-card text-left mb-8">
                  <ul className="space-y-3">
                    {trialItems.map((item: string, index: number) => (
                      <li key={index} className="flex items-start gap-3 text-yellow-200">
                        <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="fas fa-exclamation text-yellow-400 text-xs"></i>
                        </div>
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-yellow-200 mt-6 font-semibold">
                    {t('presale.disclaimer.outro')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wallet Modal */}
      <Modal isOpen={isWalletModalOpen} onClose={closeWalletModal}>
        <WalletModal onClose={closeWalletModal} onConnect={handleConnectWallet} />
      </Modal>
    </div>
  );
};